import { money } from '../format.js';
import Peek, { useMeetingContext } from '../Peek.jsx';
import { SpendingEditor, IncomeEditor, PeopleEditor } from '../Editable.jsx';
import { CashTimeline, TL_SOURCES, TL_WASH } from '../pictures.jsx';
import Callouts from '../Callouts.jsx';

// Cash flow: what comes in, against what you spend, across the years.
//
// Jake: "the income section is the whole conversation." The slide is the
// household's years as one picture, built on what Pure's planning wiki asks
// for when it reads a cash flow: income by source in every year against
// spending, with the stretches that matter marked on it (the working years,
// the first years of retirement before every benefit has started, and the
// years once every benefit has) and what savings supply in each (Jimmy,
// 2026-10-01). The picture is the slide; beneath it, a key whose figures open
// onto the questionnaire rows, and what stands out, as headings alone.

const BASIS =
  'Based on the information shown, not a complete financial plan. Before tax and before inflation; '
  + 'no investment returns or unrecorded withdrawals are modelled.';

function storySentence(t) {
  if (!t || t.retireYear === null) {
    return 'The questionnaire does not yet say when you retire, so every year is drawn as if work carries on.';
  }
  const benefit = t.socialSecurityFrom
    ? `Social Security starts in ${t.socialSecurityFrom}.`
    : t.state === 'socialSecurityMissing' ? 'Social Security is not recorded yet.' : '';
  if (t.alreadyRetired) {
    if (benefit) return `You are retired. ${benefit}`;
    return t.periods.some(p => p.key === 'steady') ? 'You are retired, and every benefit has started.' : 'You are retired.';
  }
  const retire = t.couple ? `You are both retired by ${t.retireYear}` : `You retire in ${t.retireYear}`;
  if (t.socialSecurityFrom) return `${retire}, and Social Security starts in ${t.socialSecurityFrom}.`;
  return `${retire}.${benefit ? ` ${benefit}` : ''}`;
}

const WAITING = {
  spendingMissing: 'Spending is not recorded yet.',
  incomeMissing: 'An income source has no amount yet.',
};

// What the picture's colours mean, and the way into the figures behind them:
// each name opens the questionnaire rows it is drawn from.
function Key({ t }) {
  const rows = t?.rows || [];
  const blankYear = r => t.salaryMissing && !r.retired;
  const sources = [...TL_SOURCES].reverse().filter(([key]) => rows.some(r => !blankYear(r) && r[key] > 0));
  const drawn = rows.length > 1 && !WAITING[t?.state];
  const fromSavings = drawn && t.state === 'ready' && rows.some(r => r.retired && r.spending > r.income);
  const notRecorded = drawn && (t.state === 'socialSecurityMissing' || t.salaryMissing);
  const spending = t?.spendingToday;
  return (
    <div className="conv-cash-key">
      <span className="conv-key-item is-head" data-key="income">
        <Peek title="Income" editor={() => <IncomeEditor />}>Income</Peek>
      </span>
      {sources.map(([key, label, fill]) => (
        <span className="conv-key-item" key={key} data-key={key}>
          <i style={{ background: fill }} />
          <Peek title={label} editor={() => <IncomeEditor only={[key]} />}>{label}</Peek>
        </span>
      ))}
      <span className="conv-key-item is-head" data-key="spending">
        <i className="is-line" />
        <Peek title="Annual spending" editor={() => <SpendingEditor />}>
          {spending === null || spending === undefined ? 'Spending not recorded' : `Spending, ${money(spending)} a year today`}
        </Peek>
      </span>
      {fromSavings ? <span className="conv-key-item"><i style={{ background: TL_WASH }} />From savings</span> : null}
      {notRecorded ? <span className="conv-key-item"><i className="is-hatch" />Not recorded</span> : null}
    </div>
  );
}

export default function CashFlow({ timeline = null, callouts = [] }) {
  const { people } = useMeetingContext();
  const t = timeline;
  const waiting = !t || t.rows.length < 2 ? 'There are not enough years recorded to draw.' : WAITING[t.state];
  const when = !t || t.retireYear === null ? 'Retirement not yet placed' : t.alreadyRetired ? 'Retired' : `Retiring ${t.retireYear}`;

  return (
    <>
      <div className="conv-module-head">
        <h2>What comes in, against what you spend</h2>
        <Peek title="Retirement timing" editor={() => <PeopleEditor people={people} />}>
          <span className="conv-quiet">{when}</span>
        </Peek>
      </div>
      <p className="conv-lede">{storySentence(t)}</p>

      <div className="conv-cash-chart">
        {waiting ? (
          <div className="conv-board-waiting">
            <strong>{waiting}</strong>
            <span>The picture is left open rather than drawn as if the missing figure were zero.</span>
          </div>
        ) : (
          <CashTimeline model={t} />
        )}
        <Key t={t} />
      </div>
      <p className="conv-caption">
        {BASIS}
        {t?.socialSecurityStartAssumed ? ' No Social Security starting age is recorded, so the benefit is counted from today.' : ''}
      </p>

      <Callouts items={callouts} columns={3} brief />
    </>
  );
}
